import type { PropsWithChildren } from 'react';
import ThemeToggle from '../components/ThemeToggle';
import { useBranding } from '../lib/branding';

type AuthLayoutProps = PropsWithChildren<{
  title: string;
  subtitle?: string;
}>;

export default function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  const { status, branding, effectiveTheme, setUserTheme } = useBranding();

  // Show a neutral skeleton until branding has resolved, so a project's
  // primary color and layout never flash in the wrong shape between mount
  // and the /api/auth/branding response.
  if (status === 'pending') {
    return (
      <div className="auth-shell--centered">
        <div className="auth-skeleton" aria-busy="true" aria-live="polite">
          <div className="auth-skeleton-bar auth-skeleton-bar--title" />
          <div className="auth-skeleton-bar auth-skeleton-bar--subtitle" />
          <div className="auth-skeleton-bar auth-skeleton-bar--field" />
          <div className="auth-skeleton-bar auth-skeleton-bar--field" />
          <div className="auth-skeleton-bar auth-skeleton-bar--button" />
        </div>
      </div>
    );
  }

  const themeToggle = branding.allowUserThemeOverride ? (
    <ThemeToggle effective={effectiveTheme} onChange={setUserTheme} />
  ) : null;

  if (branding.layout === 'split') {
    return (
      <div className="auth-shell--split">
        <aside className="auth-shell__brand-panel" aria-hidden="true">
          <div className="auth-shell__brand-panel-content">
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt="" className="auth-shell__brand-logo" />
            )}
            <p className="auth-shell__brand-tagline">
              {branding.appName ? `Welcome to ${branding.appName}.` : 'Welcome back.'}
            </p>
          </div>
          {branding.appName && <p className="auth-shell__brand-footer">Secured by Pryma</p>}
        </aside>
        <div className="auth-shell__form-panel">
          {themeToggle}
          <div className="auth-card">
            <h1 className="auth-title">{title}</h1>
            {subtitle ? <p className="auth-subtitle">{subtitle}</p> : null}
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell--centered">
      <div className="auth-card">
        {themeToggle}
        {branding.logoUrl && (
          <img
            src={branding.logoUrl}
            alt={branding.appName ? `${branding.appName} logo` : 'Project logo'}
            className="auth-logo"
          />
        )}
        <h1 className="auth-title">{title}</h1>
        {subtitle ? <p className="auth-subtitle">{subtitle}</p> : null}
        {children}
      </div>
    </div>
  );
}
